<script setup>
/**
 * "Offered" (MFA_PLAN.md): an account whose level is offered two-step sign-in
 * and has not set it up is asked once, in a dialog it can dismiss. "Not now"
 * holds off for 30 days per browser. Available never prompts; Enforced is the
 * wall. Mounted once in the app layout.
 */
import {onMounted, ref, watch} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import TlDialog from '~/components/TlDialog.vue';
import {useAuthService} from '~/composables/useAuthService';
import {MFA_SETUP_PATH, mfaOfferPromptDue, readOfferSnooze, snoozeOffer} from '~/utils/mfa';

const route = useRoute();
const router = useRouter();
const {user, isAuthenticated} = useAuthService();
const open = ref(false);
const mounted = ref(false);

const check = () => {
  if (!mounted.value || open.value || !isAuthenticated.value) return;
  // Not over the sign-in, set-up or another wall's step.
  if (['/security/', '/login', '/register', '/welcome/', '/prompt', '/details-required'].some((p) => route.path.startsWith(p))) return;
  if (mfaOfferPromptDue(user.value, readOfferSnooze(user.value.id))) open.value = true;
};
onMounted(() => { mounted.value = true; setTimeout(check, 1200); });
watch(() => [user.value?.id, user.value?.mfaOffer, user.value?.mfaEnrolled, route.path], () => setTimeout(check, 600));

const notNow = () => {
  if (user.value?.id) snoozeOffer(user.value.id);
  open.value = false;
};
const setUp = () => {
  // Saying yes is an answer too: do not ask again today if they back out half way.
  if (user.value?.id) snoozeOffer(user.value.id);
  open.value = false;
  router.push({path: MFA_SETUP_PATH, query: {redirect: route.fullPath}});
};
</script>

<template>
  <tl-dialog :model-value="open" title="Protect your account" :max-width="440" persistent close-on-escape close-label="Not now" @update:model-value="notNow">
    <div class="mop-ic"><v-icon size="26" color="primary">mdi-shield-check-outline</v-icon></div>
    <p class="mop-line">Add a second step to signing in: a passkey, or a code from an app on your phone. It takes a minute and keeps your account yours even if your password gets out.</p>
    <template #actions>
      <v-btn variant="text" @click="notNow">Not now</v-btn>
      <v-btn color="primary" @click="setUp">Set it up</v-btn>
    </template>
  </tl-dialog>
</template>

<style scoped>
.mop-ic { width: 48px; height: 48px; border-radius: 14px; background: rgb(var(--v-theme-brandTint)); display: flex; align-items: center; justify-content: center; margin-bottom: 12px; }
.mop-line { font-size: 14px; line-height: 1.5; color: rgb(var(--v-theme-textSubtle)); margin: 0; }
</style>
